import { Injectable, inject } from '@angular/core';
import {
    FormArray,
    FormGroup,
    UntypedFormArray,
    UntypedFormControl,
    UntypedFormGroup,
} from '@angular/forms';

import {
    FieldDefinition,
    FormElementLayoutDefinition,
    FormElementType,
    Trigger,
    WdxDestroyClass,
} from '@wdx/shared/utils';

import {
    ILayoutAndSchemaFieldDefinition,
    ReactiveFormLayoutAndDefinition,
} from '../../models';
import {
    FormConditionsService,
    FormProcessConditionDataService,
} from '../form-conditions';
import { FormControlService } from '../form-control/form-control.service';
import { FormStaticService } from '../form-static/form-static.class';
import { FormFirstLevelService } from '../form-builder/form-first-level.service';
import { FormBuilderService } from '../form-builder';
import { delay, of, take } from 'rxjs';
import { FormTriggersService } from '../form-validation';
import * as R from 'ramda';

@Injectable()
export class FormArrayService extends WdxDestroyClass {
    firstLevel = inject(FormFirstLevelService).firstLevel;
    private formStaticService = inject(FormStaticService);
    private formProcessService = inject(FormProcessConditionDataService);
    private formControlService = inject(FormControlService);
    private formBuilderService = inject(FormBuilderService);
    private formTriggersService = inject(FormTriggersService);

    private formConditionsService?: FormConditionsService;

    public setFormConditionsService(
        formConditionsService: FormConditionsService,
    ) {
        this.formConditionsService = formConditionsService;
    }

    public updateArrayToLength(
        name: string,
        newLength: number,
        formArray?: FormArray,
    ) {
        formArray =
            formArray ??
            (this.formControlService.getFormControlData(
                this.formStaticService.form,
                name,
            )?.[0]?.formControl as FormArray);
        const currentArrayLength = formArray.length;

        if (currentArrayLength === newLength) {
            return;
        }

        if (currentArrayLength > newLength) {
            // clear and recreate formcontrols to ensure old control listeners are cleared
            formArray.clear({ emitEvent: false });
            this.addToArrayByName(name, formArray, newLength, true, true);
            this.handleItemRemovedTriggers(name, formArray);
        } else {
            this.addToArrayByName(
                name,
                formArray,
                newLength - currentArrayLength,
                true,
                false,
            );
            this.handleItemAddedTriggers(name, formArray);
        }
    }

    public addToArray(
        elementLayoutDefinition: ILayoutAndSchemaFieldDefinition,
        options: {
            useFormData?: boolean;
            parentLayoutAndDef?: FormElementLayoutDefinition;
            parentIndex?: number;
            newIndex?: number;
            skipItemAddedTriggers?: boolean;
            skipApplyingTriggersOnCreate?: boolean;
            formArray?: FormArray;
        },
    ): void {
        let formArray =
            options?.formArray ??
            (this.formProcessService.getFormArray(
                this.formStaticService.form,
                options?.parentLayoutAndDef
                    ? [options.parentLayoutAndDef.name as string]
                    : [elementLayoutDefinition.name as string],
            ) as UntypedFormArray);
        const newIndex =
            options?.newIndex === undefined
                ? formArray.length
                : options?.newIndex;

        const fieldPath = options?.parentLayoutAndDef?.name
            ? `${options?.parentLayoutAndDef.name}.${elementLayoutDefinition.name}`
            : (elementLayoutDefinition.name as string);

        const subFromChildSchema = (
            this.formConditionsService?.getCurrentConfigForFieldPath(
                fieldPath,
            ) as ReactiveFormLayoutAndDefinition
        ).childSchema as FieldDefinition[];

        const layout: FormElementLayoutDefinition[] = [];
        const allFields: any[] = [];

        elementLayoutDefinition?.sectionLayoutDefinitions?.[0]?.elementLayoutDefinitions?.forEach(
            (elementLayout) => {
                const elementSchema = subFromChildSchema.find(
                    (elementSchema) =>
                        elementSchema.name === elementLayout.name,
                );

                const eleLayout = {
                    ...elementLayout,
                    isRequired: elementLayout.isHidden
                        ? false
                        : elementLayout.isRequired,
                };

                if (elementSchema) {
                    const DATA = {
                        ...elementSchema,
                        ...eleLayout,
                        ...((elementSchema as any).options && {
                            originalOptions: (elementSchema as any).options,
                        }),
                        children: [],
                    };

                    layout.push(DATA);
                    allFields.push(DATA);
                } else {
                    if (eleLayout.elementType === FormElementType.Label) {
                        layout.push(eleLayout);
                    }
                    allFields.push({
                        ...eleLayout,
                        children: [],
                    });
                }
            },
        );

        const NEW_FORM_GROUP: UntypedFormGroup =
            typeof options?.parentIndex !== 'number'
                ? this.formBuilderService.createFormGroup(
                      subFromChildSchema,
                      layout,
                      !options?.useFormData,
                      elementLayoutDefinition.name as string,
                      newIndex,
                  )
                : this.formBuilderService.createFormGroup(
                      subFromChildSchema,
                      layout,
                      !options?.useFormData,
                      elementLayoutDefinition.name as string,
                      newIndex,
                      options?.parentLayoutAndDef?.name,
                      0,
                  );

        if (typeof options?.parentIndex === 'number') {
            formArray = formArray
                .at(options.parentIndex)
                .get(
                    elementLayoutDefinition.name as string,
                ) as UntypedFormArray;
        }

        allFields.forEach((FIELD) => {
            const FORM_CONTROL = NEW_FORM_GROUP.get(
                FIELD.name,
            ) as UntypedFormControl;

            if (FIELD.defaultValue) {
                this.formProcessService.updateFormControlValue(
                    FORM_CONTROL,
                    FIELD.defaultValue,
                    true,
                );
            }
            this.formProcessService.updateValidation(FORM_CONTROL, FIELD);
        });

        this.firstLevel[elementLayoutDefinition.name as string].layout.push(
            elementLayoutDefinition.childSchema,
        );

        this.formConditionsService?.addArrayItem(
            elementLayoutDefinition as ReactiveFormLayoutAndDefinition,
            layout as ReactiveFormLayoutAndDefinition[],
        );

        formArray.push(NEW_FORM_GROUP);

        const TRIGGERS =
            this.formStaticService.formDefinition?.conditions?.triggers;

        setTimeout(() => {
            this.applyTriggersToChildControls(
                TRIGGERS,
                elementLayoutDefinition.name as string,
                NEW_FORM_GROUP,
                newIndex,
                options?.skipApplyingTriggersOnCreate,
            );
        });

        if (!options?.skipItemAddedTriggers) {
            this.handleItemAddedTriggers(
                elementLayoutDefinition.name as string,
                formArray,
            );
        }
    }

    public removeFromArray(
        element: ReactiveFormLayoutAndDefinition,
        options: {
            index: number;
            parentName?: string | null;
            parentIndex?: number;
            skipItemRemovedTriggers?: boolean;
            formArray?: FormArray;
        },
    ): void {
        let formArray =
            options.formArray ??
            (this.formProcessService.getFormArray(
                this.formStaticService.form,
                options?.parentName
                    ? [options.parentName as string]
                    : [element.name as string],
            ) as UntypedFormArray);

        if (typeof options?.parentIndex === 'number') {
            formArray = formArray
                .at(options.parentIndex)
                .get(element.name as string) as UntypedFormArray;
        }

        const layout = this.firstLevel[element.name as string]?.layout;
        const layoutAndDefinition = element.children?.[0].layoutAndDefinition;

        this.firstLevel[element.name as string].layout = [
            ...layout.slice(0, options?.index),
            ...layout.slice(options?.index + 1),
        ];

        this.formConditionsService?.removeArrayItem(
            element as ReactiveFormLayoutAndDefinition,
            options?.index,
        );

        element.children![0].layoutAndDefinition = [
            ...(layoutAndDefinition?.slice(0, options?.index) || []),
            ...(layoutAndDefinition?.slice(options?.index + 1) || []),
        ] as ReactiveFormLayoutAndDefinition[][];
        formArray.removeAt(options?.index);

        if (!options?.skipItemRemovedTriggers) {
            this.handleItemRemovedTriggers(element.name as string, formArray);
        }
    }

    public applyTriggersToChildControls(
        triggers: Trigger[] | null | undefined,
        formGroupPath: string,
        newFormGroup: FormGroup,
        newIndex?: number,
        skipApplyingTriggersOnCreate?: boolean,
    ) {
        const pathWithoutSubformIndex = formGroupPath?.replaceAll(
            /\[\d*]/g,
            '',
        );
        triggers?.forEach((trigger) => {
            // triggers that update new child controls
            if (
                !skipApplyingTriggersOnCreate &&
                trigger.is?.some((is) =>
                    is?.then?.updateFields?.some(({ name }) =>
                        name?.startsWith(`${pathWithoutSubformIndex}.`),
                    ),
                )
            ) {
                let triggerFieldValue;
                // trigger is also from the same parent (subform or address)
                if (trigger.field?.startsWith(`${pathWithoutSubformIndex}.`)) {
                    const triggerFieldParts = trigger.field.split('.');
                    triggerFieldValue =
                        triggerFieldParts.length === 2
                            ? newFormGroup.controls[triggerFieldParts[1]]?.value
                            : (
                                  newFormGroup.controls[
                                      triggerFieldParts[1]
                                  ] as FormGroup
                              )?.controls[triggerFieldParts[2]]?.value;
                } else if (this.formTriggersService.useContext(trigger)) {
                    triggerFieldValue =
                        this.formTriggersService.getContextTriggerValue(
                            trigger,
                            this.formStaticService?.formData?.context ?? {},
                        );
                } else {
                    // trigger is not from current parent - e.g. a root level control
                    triggerFieldValue =
                        this.formControlService.getFormControlData(
                            this.formStaticService.form,
                            trigger.field as string,
                        )?.[0]?.formControl?.value;
                }

                this.formConditionsService?.applyIsTrigger(
                    trigger,
                    triggerFieldValue,
                    {
                        index: newIndex,
                        formControl: undefined as any,
                    },
                );
            }

            //triggers to add to new controls
            if (trigger.field?.startsWith(`${pathWithoutSubformIndex}.`)) {
                const triggerFieldParts = trigger.field.split('.');

                if (triggerFieldParts.length > 1) {
                    const leafName = R.last(triggerFieldParts);
                    const triggerControl =
                        newFormGroup.controls[leafName as string];
                    if (triggerControl) {
                        this.formConditionsService?.setFormControlListener(
                            trigger,
                            {
                                index: newIndex,
                                formControl: triggerControl,
                            },
                        );
                    }
                }
            }
        }, 0);

        const childFieldDefaults =
            this.formStaticService.formDefinition?.conditions?.defaults?.filter(
                ({ field }) => field?.startsWith(`${pathWithoutSubformIndex}.`),
            );

        of(null)
            .pipe(delay(0), delay(0), take(1))
            .subscribe(() => {
                const childVisibilityRules =
                    this.formStaticService.formDefinition?.conditions?.visibility?.filter(
                        (visRule) =>
                            visRule?.field?.startsWith(
                                `${pathWithoutSubformIndex}.`,
                            ),
                    );
                this.formConditionsService?.updateFormVisibilityAndListeners(
                    childVisibilityRules,
                    newIndex,
                );
                const childMandatoryRules =
                    this.formStaticService.formDefinition?.conditions?.mandatory?.filter(
                        (rule) =>
                            rule?.field?.startsWith(
                                `${pathWithoutSubformIndex}.`,
                            ),
                    );
                this.formConditionsService?.updateFormMandatoryAndListeners(
                    childMandatoryRules,
                    newIndex,
                );
                this.formConditionsService?.setDefaultFieldValues(
                    childFieldDefaults,
                    newIndex,
                );
            });
    }

    private addToArrayByName(
        name: string,
        formArray: FormArray,
        numberToAdd = 1,
        skipItemAddedTriggers = false,
        skipApplyingTriggersOnCreate = false,
    ) {
        const arrayDef =
            this.formStaticService.getFieldDefinitionForFieldFromFormDef(name);
        const layout =
            this.formStaticService.getLayoutForFieldFromFormDef(name);
        const elementLayoutDefinition: ILayoutAndSchemaFieldDefinition = {
            ...arrayDef,
            elementType: layout?.elementType as FormElementType,
            sectionLayoutDefinitions: layout?.sectionLayoutDefinitions,
        };
        for (let index = 0; index < numberToAdd; index++) {
            this.addToArray(elementLayoutDefinition, {
                skipItemAddedTriggers,
                formArray,
                skipApplyingTriggersOnCreate,
            });
        }
    }

    private handleItemAddedTriggers(name: string, formArray: FormArray) {
        const itemAddedTriggers =
            this.formStaticService.formDefinition?.conditions?.triggers?.filter(
                (trigger) => trigger.field === name && trigger.itemAdded,
            );
        itemAddedTriggers?.forEach((trigger) => {
            this.formConditionsService?.applyConditions(trigger, {
                formControl: formArray,
            });
        });
    }

    private handleItemRemovedTriggers(name: string, formArray: FormArray) {
        const itemRemovedTriggers =
            this.formStaticService.formDefinition?.conditions?.triggers?.filter(
                (trigger) => trigger.field === name && trigger.itemRemoved,
            );
        itemRemovedTriggers?.forEach((trigger) => {
            this.formConditionsService?.applyConditions(trigger, {
                formControl: formArray,
            });
        });
    }
}
